import { Settings, GATSBY_DEV_ENV } from '../app/Settings'
import { LoggerAnalytics } from '@codeleap/common'
import { getFirebase } from './firebaseApp'
import { QueryKeys } from './api/session/queryKeys'
import { Organisation, OverallScoreRank, Profile } from '@/types'
import { IS_SSR } from '../app/constants'
import { AnalyticsBrowser } from '@segment/analytics-next'
import * as TestPanel from '../app/testPanel'
import { isImpersonating } from '@/stores/impersonate'
import { sessionReplayManager } from './amplitudeSessionReplay'

const enabled = Settings.EnableAnalytics && !IS_SSR

type User = Profile & {
  badges?: string
  organisationRank?: OverallScoreRank
  score?: Organisation['score']
  utm?: string
}

export const analytics = AnalyticsBrowser.load({
  writeKey: GATSBY_DEV_ENV || TestPanel.isEnabled() ? Settings.ApiCredentials.Segment.DEV : Settings.ApiCredentials.Segment.PROD,
})

const server = (GATSBY_DEV_ENV || TestPanel.isEnabled()) ? 'development' : 'production'

if (analytics) {
  const originalTrack = analytics.track

  analytics.track = (event, ...args) => {
    event += '_w'
    args[0] = {
      ...(args?.[0] || {}),
      server,
      ...sessionReplayManager.getCurrentSessionReplayProperties(),
    }
    if (isImpersonating()) return
    logger.log(event, args)
    return originalTrack.apply(this, [event, ...args])
  }

  const originalIdentify = analytics.identify

  analytics.identify = ({ user, isLoggedIn }: { user: User; isLoggedIn: boolean }) => {
    if (isImpersonating()) return

    sessionReplayManager.identify(user?.id)

    return originalIdentify.apply(this, [user?.id, {
      id: user?.id,
      email: user?.email,
      address: {
        country: user?.country,
      },
      company: {
        name: user?.organization?.name,
        id: user?.organization?.id,
        employee_count: user?.organization?.max_employees,
        industry: user?.organization?.category?.label,
      },
      badges: user?.badges,
      gender: user?.gender,
      username: user?.display_name,
      name: user?.name,
      advertising_source: user?.inbound_source,
      climate_priority: user?.climate_priority,
      satisfaction_at_work: user?.satisfaction_at_work,
      date_of_birth: user?.birthday,
      function: user?.department,
      seniority: user?.seniority,
      logged_in: isLoggedIn,
      server,
      climate_empowerment_score: user?.organisationRank?.label,
      climate_empowerment_number: user?.score?.overall,
      head_score: user?.score?.categories[1]?.total,
      heart_score: user?.score?.categories[2]?.total,
      hands_Score: user?.score?.categories[3]?.total,
      initial_utm: user?.utm,
    }])
  }
}

export const AppAnalytics = new LoggerAnalytics.Analytics({
  init: async () => {
    if (!enabled) return
    if (isImpersonating()) return

    const fb = await getFirebase()

    const analytics = fb.analytics()

    analytics.setAnalyticsCollectionEnabled(true)

    analytics.logEvent('page_view', {})
  },
  onEvent: async (eventArgs) => {
    if (!enabled) return
    if (isImpersonating()) return

    const fb = await getFirebase()
    const analytics = fb.analytics()
    analytics.logEvent('app_action', eventArgs)

  },
  prepareData: () => {
    const me = QueryKeys.me.getData()

    getFirebase().then(fb => {
      fb.analytics().setUserId(me?.id ?? null, { global: true })
    })

    if (!enabled || !me) return
    if (isImpersonating()) return

    const extraData = {
      user: me.id,
    }

    return extraData
  },
  onInteraction: async (eventArgs) => {
    if (!enabled) return
    if (isImpersonating()) return

    const fb = await getFirebase()
    const analytics = fb.analytics()
    analytics.logEvent('user_interaction', eventArgs)

  },
}, Settings)
